import React, { useEffect, useRef } from "react";
import { createPopup, Popup } from "@typeform/embed";
import "@typeform/embed/build/css/popup.css";

const popupStyles = `
  .tf-v1-popup-container {
    max-width: 80vw !important;
    max-height: 80vh !important;
    width: 80vw !important;
    height: 80vh !important;
    border-radius: 12px !important;
    overflow: hidden !important;
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    z-index: 10000 !important;
  }
  
  .tf-v1-popup-wrapper {
    border-radius: 12px !important;
    overflow: hidden !important;
    max-width: 80vw !important;
    max-height: 80vh !important;
    width: 80vw !important;
    height: 80vh !important;
  }
  
  .tf-v1-iframe {
    border-radius: 12px !important;
    max-width: 80vw !important;
    max-height: 80vh !important;
    width: 80vw !important;
    height: 80vh !important;
  }

  /* Mobile-specific padding */
  @media (max-width: 768px) {
    .tf-v1-popup {
      padding: 40px !important;
    }
    
    .tf-v1-popup-container {
      max-width: calc(100vw - 32px) !important;
      width: calc(100vw - 32px) !important;
      left: 16px !important;
      right: 16px !important;
      transform: translateY(-50%) !important;
      margin: 0 !important;
    }
    
    .tf-v1-popup-wrapper {
      max-width: calc(100vw - 32px) !important;
      width: calc(100vw - 32px) !important;
    }
    
    .tf-v1-iframe {
      max-width: calc(100vw - 32px) !important;
      width: calc(100vw - 32px) !important;
    }
  }

  /* Override any fullscreen mobile styles */
  .tf-v1-popup-container.tf-v1-popup-mobile {
    max-width: 80vw !important;
    max-height: 80vh !important;
    width: 80vw !important;
    height: 80vh !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    position: fixed !important;
  }
  
  @media (max-width: 768px) {
    .tf-v1-popup-container.tf-v1-popup-mobile {
      max-width: calc(100vw - 32px) !important;
      width: calc(100vw - 32px) !important;
      left: 16px !important;
      right: 16px !important;
      transform: translateY(-50%) !important;
    }
  }
`;

interface GlobalTypeformPopupProps {
  typeformId: string;
  delay?: number;
  respectCookieConsent?: boolean;
  hide_on_specific_pages?: string[];
  preventReopenDays?: number;
  forceShow?: boolean;
  display_on_specific_content_types?: string[];
  display_on_specific_pages?: string[];
  currentStoryUid?: string;
  currentContentType?: string;
}

const GlobalTypeformPopup: React.FC<GlobalTypeformPopupProps> = ({
  typeformId,
  delay = 5,
  respectCookieConsent = false,
  hide_on_specific_pages = [],
  preventReopenDays = 7,
  forceShow = true,
  display_on_specific_pages = [],
  currentStoryUid,
  display_on_specific_content_types = [],
  currentContentType,
}) => {
  const popupRef = useRef<Popup | null>(null);
  const storageKey = `typeform_popup_${typeformId}_suppress_until`;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const styleElement = document.createElement("style");
    styleElement.textContent = popupStyles;
    document.head.appendChild(styleElement);

    // Hierarchical display logic - page-specific settings override content type settings
    if (currentStoryUid) {
      // 1. HIDE ON SPECIFIC PAGES - highest priority (overrides everything)
      if (hide_on_specific_pages && hide_on_specific_pages.length > 0) {
        const shouldHide = hide_on_specific_pages.includes(currentStoryUid);
        if (shouldHide) return;
      }

      // 2. DISPLAY ON SPECIFIC PAGES - second priority (overrides content type)
      if (display_on_specific_pages && display_on_specific_pages.length > 0) {
        const shouldDisplay = display_on_specific_pages.includes(currentStoryUid);
        if (shouldDisplay) {
          // This page is specifically whitelisted, continue to show popup
        } else {
          // This page is not in the whitelist, don't show
          return;
        }
      } else {
        // 3. DISPLAY ON SPECIFIC CONTENT TYPES - lowest priority (only if no page-specific rules)
        if (display_on_specific_content_types && display_on_specific_content_types.length > 0 && currentContentType) {
          const shouldDisplayContentType = display_on_specific_content_types.includes(currentContentType);
          if (!shouldDisplayContentType) {
            // Content type is not whitelisted, don't show
            return;
          }
        }
      }
    }

    // Keep path-based logic as fallback for when story UID isn't available
    if (!currentStoryUid && hide_on_specific_pages && hide_on_specific_pages.length > 0) {
      const currentPath = window.location.pathname;
      const shouldHide = hide_on_specific_pages.some(page => {
        const exactMatch = currentPath === page;
        const pathStartsWithExclusion = currentPath.startsWith(page) && page !== "/";
        return exactMatch || pathStartsWithExclusion;
      });

      if (shouldHide) return;
    }

    // Fallback: Check if current page should be displayed (path-based)
    if (!currentStoryUid && display_on_specific_pages && display_on_specific_pages.length > 0) {
      const currentPath = window.location.pathname;
      const shouldDisplay = display_on_specific_pages.some(page => {
        const exactMatch = currentPath === page;
        const pathStartsWithInclusion = currentPath.startsWith(page) && page !== "/";
        return exactMatch || pathStartsWithInclusion;
      });

      if (!shouldDisplay) return;
    }

    if (!forceShow && preventReopenDays) {
      try {
        const until = Number(localStorage.getItem(storageKey) || 0);
        if (until && Date.now() < until) {
          return;
        }
      } catch {}
    }

    popupRef.current = createPopup(typeformId, {
      open: "time",
      openValue: Math.max(0, delay * 1000),
      preventReopenOnClose: true,
      width: "80vw",
      height: "80vh",
      onClose: () => {
        if (preventReopenDays && !forceShow) {
          try {
            const until = Date.now() + preventReopenDays * 24 * 60 * 60 * 1000;
            localStorage.setItem(storageKey, String(until));
          } catch {}
        }
      },
      onSubmit: () => {
        if (preventReopenDays && !forceShow) {
          try {
            const until = Date.now() + preventReopenDays * 24 * 60 * 60 * 1000;
            localStorage.setItem(storageKey, String(until));
          } catch {}
        }
      },
    });

    return () => {
      try {
        popupRef.current?.unmount?.();
      } catch {}
      popupRef.current = null;

      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, [
    typeformId,
    delay,
    respectCookieConsent,
    hide_on_specific_pages,
    preventReopenDays,
    forceShow,
    display_on_specific_pages,
    currentStoryUid,
    display_on_specific_content_types,
    currentContentType,
  ]);

  return null;
};

export default GlobalTypeformPopup;
